/**
 * Payment Component
 *
 * Handles Guesty Pay tokenization using the @guestyorg/tokenization-js SDK.
 * The SDK renders a complete payment form in an iframe for PCI compliance.
 *
 * Flow:
 * 1. loadGuestyPaySDK() - Load the SDK
 * 2. renderPaymentForm() - Render form in container
 * 3. submitPaymentForm() - Submit and get token
 * 4. destroyPaymentForm() - Cleanup
 */

let guestyTokenization = null;
let sdkLoaded = false;
let sdkLoading = false;
let sdkLoadPromise = null;
let formRendered = false;
const DEBUG_LOGGING = false;

function debugLog(...args) {
    if (DEBUG_LOGGING) {
        console.log(...args);
    }
}

function debugError(...args) {
    if (DEBUG_LOGGING) {
        console.error(...args);
    }
}

/**
 * Load the Guesty Pay tokenization SDK.
 *
 * @param {Object} options - SDK loading options
 * @param {boolean} options.sandbox - Load SDK in sandbox mode (default: false)
 * @param {string} options.version - SDK version (default: 'v1')
 * @returns {Promise<Object>} The Guesty tokenization SDK instance
 */
export async function loadGuestyPaySDK(options = {}) {
    if (sdkLoaded && guestyTokenization) {
        return guestyTokenization;
    }

    if (sdkLoading) {
        // Wait for existing load to complete
        return new Promise((resolve, reject) => {
            const checkInterval = setInterval(() => {
                if (sdkLoaded && guestyTokenization) {
                    clearInterval(checkInterval);
                    resolve(guestyTokenization);
                }
            }, 100);
            // Timeout after 10 seconds
            setTimeout(() => {
                clearInterval(checkInterval);
                reject(new Error('SDK loading timeout'));
            }, 10000);
        });
    }

    sdkLoading = true;

    try {
        // Load SDK directly from pay.guesty.com to remain PCI compliant.
        // See https://pay.guesty.com/tokenization/v2/init.js
        const version = options.version || 'v2';
        const baseUrl = options.sandbox
            ? 'https://pay.sandbox.guesty.com'
            : 'https://pay.guesty.com';
        const scriptSrc = `${baseUrl}/tokenization/${version}/init.js`;

        if (!sdkLoadPromise) {
            sdkLoadPromise = new Promise((resolve, reject) => {
                if (window.guestyTokenization) {
                    resolve(window.guestyTokenization);
                    return;
                }

                const existing = document.querySelector(
                    `script[src="${scriptSrc}"]`
                );
                if (existing) {
                    existing.addEventListener('load', () =>
                        resolve(window.guestyTokenization)
                    );
                    existing.addEventListener('error', () =>
                        reject(new Error('SDK script load failed'))
                    );
                    return;
                }

                const script = document.createElement('script');
                script.src = scriptSrc;
                script.async = true;
                script.onload = () => resolve(window.guestyTokenization);
                script.onerror = () =>
                    reject(new Error('SDK script load failed'));
                document.head.appendChild(script);
            });
        }

        guestyTokenization = await sdkLoadPromise;

        sdkLoaded = true;
        sdkLoading = false;

        debugLog('Guesty Pay SDK (v2) loaded successfully');
        return guestyTokenization;
    } catch (error) {
        sdkLoading = false;
        console.error('Failed to load Guesty Pay SDK:', error);
        throw new Error(
            'Failed to load payment SDK. Please refresh the page and try again.'
        );
    }
}

/**
 * Render the Guesty payment form in a container.
 *
 * @param {Object} options - Render options
 * @param {string} options.containerId - ID of the container element
 * @param {string} options.paymentProviderId - Payment provider ID from Guesty
 * @param {number} options.amount - Payment amount
 * @param {string} options.currency - Currency code (e.g., 'USD')
 * @param {Function} options.onValidChange - Callback when form validity changes
 * @param {Object} options.initialValues - Initial form values (optional)
 * @param {Object} options.style - Custom style variables (optional)
 * @returns {Promise<void>}
 */
export async function renderPaymentForm(options) {
    try {
        const sdk = await loadGuestyPaySDK();

        // Destroy existing form if any
        if (formRendered) {
            try {
                sdk.destroy();
            } catch {
                // Ignore destroy errors
            }
            formRendered = false;
        }

        const renderOptions = {
            containerId: options.containerId,
            providerId: options.paymentProviderId,
            listingId: options.listingId,
            amount: typeof options.amount === 'number' ? options.amount : 1,
            currency: options.currency || 'USD',
            lang: 'en',
            showSupportedCards: true,
            showPciNote: true,
            sectionsOrder: ['card', 'billing'],
            fontSrc:
                'https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,200..800&display=swap',
            styles: {
                fontFamily: '"Bricolage Grotesque", sans-serif',
                fontSizeMd: '14px',
                fontSizeLg: '16px',
                fontWeightRegular: '400',
                fontWeightBold: '600',
                borderRadius: '8px',
                inputHeight: '48px',
                inputPadding: '0 12px',
                colorText: '#291e03',
                colorTextError: '#e53e3e',
                colorPlaceholder: '#797265',
                colorBorder: '#291E03',
                colorBorderError: '#CA0101',
                colorBorderHover: '#CCB1A0',
                colorFormBackground: 'transparent',
                colorBackground: '#ffffff',
                colorBackgroundError: '#fff5f5',
                colorBackgroundDisabled: '#edf2f7',
                mobileBreakpoint: 480
            }
        };

        // Add validity change callback
        if (options.onValidChange) {
            renderOptions.onValidChange = options.onValidChange;
        }

        // Add initial values if provided
        if (options.initialValues) {
            renderOptions.initialValues = options.initialValues;
            renderOptions.showInitialValuesToggle = false;
        }

        // Add custom styles to match Avari design
        renderOptions.style = {
            '--gp-font-family': 'inherit',
            '--gp-font-size': '16px',
            '--gp-font-size-lg': '18px',
            '--gp-font-weight': '400',
            '--gp-font-weight-bold': '500',
            '--gp-input-border-radius': '0.75rem',
            '--gp-input-height': '56px',
            '--gp-input-padding': '0 16px',
            '--gp-color-text': '#291E03',
            '--gp-color-error': '#dc2626',
            '--gp-color-placeholder': '#9ca3af',
            '--gp-color-border': '#291E03',
            '--gp-color-border-error': '#dc2626',
            '--gp-color-border-hover': '#441D1D',
            '--gp-color-bg': '#ffffff',
            '--gp-color-bg-input': '#ffffff',
            '--gp-color-bg-input-error': '#fef2f2',
            '--gp-color-bg-input-disabled': '#f3f4f6',
            ...options.style
        };

        debugLog('Rendering payment form (v2) with options:', {
            ...renderOptions,
            paymentProviderId: '***'
        });

        await sdk.render(renderOptions);
        formRendered = true;
        debugLog('Payment form rendered successfully');
    } catch (error) {
        debugError('Failed to render payment form:', error);
        throw new Error(
            'Failed to load payment form. Please refresh the page and try again.'
        );
    }
}

/**
 * Validate the payment form.
 *
 * @returns {void}
 */
export function validatePaymentForm() {
    if (!guestyTokenization || !formRendered) {
        console.warn('Payment form not rendered');
        return;
    }
    guestyTokenization.validate();
}

/**
 * Submit the payment form and get the token/paymentMethodId.
 *
 * @param {Object} payload - Data to submit (required for v2, e.g. guest info)
 * @returns {Promise<string>} Payment Method ID (was ccToken in v1)
 */
export async function submitPaymentForm(payload = {}) {
    if (!guestyTokenization || !formRendered) {
        throw new Error('Payment form not initialized. Please try again.');
    }

    try {
        // SDK v2 submit requires payload
        const result = await guestyTokenization.submit(payload);

        // Check if 3DS authentication is required first
        if (result?.threeDS?.authURL) {
            // Redirect to 3DS authentication
            debugLog('3DS authentication required, redirecting...');
            window.location.href = result.threeDS.authURL;
            return null; // Will redirect
        }

        // Extract ID from result - check multiple possible locations
        // In v2 this is typically 'id' or '_id' and represents the Payment Method ID
        const paymentMethodId =
            result?._id ||
            result?.id ||
            result?.token ||
            result?.ccToken ||
            result?.paymentToken ||
            result?.data?._id ||
            result?.data?.id ||
            result?.data?.token;

        const confirmationToken =
            result?.confirmationToken ||
            result?.data?.confirmationToken ||
            result?.paymentMethod?.confirmationToken ||
            null;

        if (!paymentMethodId) {
            debugError(
                'No payment method ID found in response',
                Object.keys(result || {})
            );
            throw new Error(
                'Payment tokenization failed. No ID received from payment provider.'
            );
        }

        // Extract Card Metadata (Brand, Last4)
        // Adjust these paths based on actual SDK response structure
        const brand =
            result?.card?.brand ||
            result?.paymentMethod?.card?.brand ||
            result?.ccType ||
            result?.data?.card?.brand ||
            'Card';

        const last4 =
            result?.card?.last4 ||
            result?.paymentMethod?.card?.last4 ||
            result?.lastFour ||
            result?.data?.card?.last4 ||
            '****';

        const saveForFutureUseRaw =
            result?.saveForFutureUse ??
            result?.reuse ??
            result?.paymentMethod?.saveForFutureUse ??
            result?.paymentMethod?.reuse ??
            result?.data?.saveForFutureUse ??
            result?.data?.reuse ??
            null;

        const saveForFutureUse = !!saveForFutureUseRaw;

        return {
            id: paymentMethodId,
            confirmationToken: confirmationToken,
            brand: brand,
            last4: last4,
            saveForFutureUse: saveForFutureUse,
            saveForFutureUseRaw: saveForFutureUseRaw,
            resultKeys: Object.keys(result || {}),
            rawFlags: {
                saveForFutureUse: result?.saveForFutureUse ?? null,
                reuse: result?.reuse ?? null,
                paymentMethodSaveForFutureUse:
                    result?.paymentMethod?.saveForFutureUse ?? null,
                paymentMethodReuse: result?.paymentMethod?.reuse ?? null,
                dataSaveForFutureUse: result?.data?.saveForFutureUse ?? null,
                dataReuse: result?.data?.reuse ?? null
            }
        };
    } catch (error) {
        debugError('Payment form submission error:', error);

        // Handle specific error types
        if (error.message) {
            throw error;
        }

        throw new Error(
            'Payment processing failed. Please check your card details and try again.'
        );
    }
}

/**
 * Destroy the payment form and cleanup.
 */
export function destroyPaymentForm() {
    if (guestyTokenization && formRendered) {
        try {
            guestyTokenization.destroy();
            formRendered = false;
            debugLog('Payment form destroyed');
        } catch (error) {
            debugError('Error destroying payment form:', error);
        }
    }
}

/**
 * Check if the payment form is currently rendered.
 *
 * @returns {boolean}
 */
export function isPaymentFormRendered() {
    return formRendered;
}

/**
 * Validate card number using Luhn algorithm.
 *
 * @param {string} cardNumber - Card number to validate
 * @returns {boolean} True if card number is valid
 */
export function validateCardNumber(cardNumber) {
    const cleaned = cardNumber.replace(/\s+/g, '');
    if (!/^\d{13,19}$/.test(cleaned)) {
        return false;
    }

    let sum = 0;
    let isEven = false;

    for (let i = cleaned.length - 1; i >= 0; i--) {
        let digit = parseInt(cleaned[i], 10);

        if (isEven) {
            digit *= 2;
            if (digit > 9) {
                digit -= 9;
            }
        }

        sum += digit;
        isEven = !isEven;
    }

    return sum % 10 === 0;
}

/**
 * Format card number with spaces (e.g., "4242 4242 4242 4242").
 *
 * @param {string} cardNumber - Card number to format
 * @returns {string} Formatted card number
 */
export function formatCardNumber(cardNumber) {
    const cleaned = cardNumber.replace(/\s+/g, '');
    const groups = cleaned.match(/.{1,4}/g);
    return groups ? groups.join(' ') : cleaned;
}

/**
 * Validate expiration date.
 *
 * @param {string} month - Expiration month (MM)
 * @param {string} year - Expiration year (YYYY)
 * @returns {boolean} True if expiration date is valid and not expired
 */
export function validateExpirationDate(month, year) {
    const monthNum = parseInt(month, 10);
    const yearNum = parseInt(year, 10);

    if (monthNum < 1 || monthNum > 12) {
        return false;
    }

    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;

    if (yearNum < currentYear) {
        return false;
    }

    if (yearNum === currentYear && monthNum < currentMonth) {
        return false;
    }

    return true;
}

/**
 * Validate CVC code.
 *
 * @param {string} cvc - CVC code
 * @returns {boolean} True if CVC is valid (3-4 digits)
 */
export function validateCVC(cvc) {
    return /^\d{3,4}$/.test(cvc);
}
